import React, { useState, useEffect } from 'react';
import { createSouscription, updateSouscription, createSouscriptionWithProforma, updateSouscriptionWithProforma } from '../../services/souscriptionService';
import { getLogementsDisponibles } from '../../services/logementService';
import { logementService } from '../../services/logementService';
import { generateProforma, downloadPdf, previewPdf } from '../../services/proformaService';
import { getAllServices } from '../../services/serviceService';
import SpinLoader from '../common/SpinLoader';
import ProcessingLoader from '../common/ProcessingLoader';
import SuccessModal from '../common/SuccessModal';
import ErrorModal from '../common/ErrorModal';
import GoogleAutocomplete from '../common/GoogleAutocomplete';
import PhoneInput from '../common/PhoneInput';
import GenderRadio from '../common/GenderRadio';
import MapSelector from '../common/MapSelector';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useAuth } from '../../contexts/AuthContext';

const WizardSouscription = ({ souscriptionToEdit, onComplete, onCancel }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [logements, setLogements] = useState([]);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [proformaLoading, setProformaLoading] = useState(false);
  const [proformaData, setProformaData] = useState(null);

  // États pour les nouveaux modals
  const [processingLoader, setProcessingLoader] = useState(false);
  const [successModal, setSuccessModal] = useState({ isOpen: false, title: '', message: '', email: '' });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '', details: '' });

  // États pour gérer les dépendances géographiques
  const [selectedCountryCode, setSelectedCountryCode] = useState('');
  const [selectedSchoolCountryCode, setSelectedSchoolCountryCode] = useState('');

  // État pour suivre la validation des champs d'adresse
  const [addressValidationStatus, setAddressValidationStatus] = useState({});

  // États pour la sélection par carte
  const [selectedMapAccommodation, setSelectedMapAccommodation] = useState(null);
  const [mapSelectionMode, setMapSelectionMode] = useState(false);
  
  const [formData, setFormData] = useState(() => {
    if (souscriptionToEdit) {
      return {
        nom_client: souscriptionToEdit.nom_client || '',
        prenom_client: souscriptionToEdit.prenom_client || '',
        email_client: souscriptionToEdit.email_client || '',
        telephone_client: souscriptionToEdit.telephone_client || '',
        genre_client: souscriptionToEdit.genre_client || '',
        date_naissance_client: souscriptionToEdit.date_naissance_client || '',
        ville_naissance_client: souscriptionToEdit.ville_naissance_client || '',
        pays_naissance_client: souscriptionToEdit.pays_naissance_client || '',
        nationalite_client: souscriptionToEdit.nationalite_client || '',
        pays_destination: souscriptionToEdit.pays_destination || '',
        date_arrivee_prevue: souscriptionToEdit.date_arrivee_prevue || '',
        ecole_universite: souscriptionToEdit.ecole_universite || '',
        filiere: souscriptionToEdit.filiere || '',
        pays_ecole: souscriptionToEdit.pays_ecole || '',
        ville_ecole: souscriptionToEdit.ville_ecole || '',
        code_postal_ecole: souscriptionToEdit.code_postal_ecole || '',
        adresse_ecole: souscriptionToEdit.adresse_ecole || '',
        coordonnees_gps_ecole: souscriptionToEdit.coordonnees_gps_ecole || '',
        logement_id: souscriptionToEdit.logement_id || '',
        date_entree_prevue: souscriptionToEdit.date_entree_prevue || '',
        duree_location_mois: souscriptionToEdit.duree_location_mois || 12
      };
    } else {
      // Création : pré-remplir l'email avec l'utilisateur connecté
      return {
        nom_client: '',
        prenom_client: '',
        email_client: user?.email || '',  // Auto-remplissage
        telephone_client: '',
        genre_client: '',
        date_naissance_client: '',
        ville_naissance_client: '',
        pays_naissance_client: '',
        nationalite_client: '',
        pays_destination: '',
        date_arrivee_prevue: '',
        ecole_universite: '',
        filiere: '',
        pays_ecole: '',
        ville_ecole: '',
        code_postal_ecole: '',
        adresse_ecole: '',
        coordonnees_gps_ecole: '',
        logement_id: '',
        date_entree_prevue: '',
        duree_location_mois: 12
      };
    }
  });

  // Auto-chargement des logements à l'étape 3 et des services/logements à l'étape 4
  useEffect(() => {
    if (currentStep === 3) {
      // Charger les logements automatiquement à l'étape 3
      if (logements.length === 0) {
        loadLogements();
      }
    } else if (currentStep === 4) {
      // Charger les données si nécessaire à l'étape 4
      if (services.length === 0) {
        loadServices();
      }
      if (logements.length === 0) {
        loadLogements();
      }
    }
  }, [currentStep]);

  // Vérifier et réinitialiser le logement_id si le logement sélectionné n'est plus disponible
  // MAIS ne pas vérifier si on est en mode sélection carte (pour éviter les conflits)
  useEffect(() => {
    if (logements.length > 0 && formData.logement_id && !mapSelectionMode) {
      const logementExists = logements.find(l => l.id === parseInt(formData.logement_id));
      if (!logementExists) {
        // Le logement sélectionné n'est plus disponible, le réinitialiser
        setFormData(prev => ({
          ...prev,
          logement_id: ''
        }));
        setGeneralError('Le logement précédemment sélectionné n\'est plus disponible. Veuillez choisir un autre logement.');
      }
    }
  }, [logements, formData.logement_id, mapSelectionMode]);

  // Initialiser les données de sélection en mode édition
  useEffect(() => {
    if (souscriptionToEdit && logements.length > 0 && formData.logement_id && !selectedMapAccommodation) {
      const selectedLogement = logements.find(l => l.id === parseInt(formData.logement_id));

      if (selectedLogement) {
        // Vérifier si ce logement a des coordonnées GPS qui indiquent qu'il a été créé depuis la carte
        if (selectedLogement.coordonnees_gps && selectedLogement.cood_gps_standart) {
          // Ce logement semble avoir été créé depuis la carte, le traiter comme une sélection carte
          const [lat, lng] = selectedLogement.cood_gps_standart.split(',').map(coord => parseFloat(coord.trim()));

          const mapAccommodationData = {
            id: selectedLogement.id,
            title: selectedLogement.titre,
            description: selectedLogement.description,
            address: selectedLogement.adresse,
            ville: selectedLogement.ville,
            pays: selectedLogement.pays,
            code_postal: selectedLogement.code_postal,
            price: selectedLogement.loyer,
            charges: selectedLogement.montant_charges,
            coordinates: selectedLogement.coordonnees_gps,
            position: { lat, lng },
            distance: 0, // Calculé dynamiquement si nécessaire
            isExisting: true
          };

          setSelectedMapAccommodation(mapAccommodationData);
          setMapSelectionMode(true);
        }
      }
    }
  }, [souscriptionToEdit, logements, formData.logement_id, selectedMapAccommodation]);

  // Déclencher la génération automatique quand les données sont prêtes à l'étape 4
  useEffect(() => {
    if (currentStep === 4 && services.length > 0 && logements.length > 0) {
      const selectedLogement = logements.find(l => l.id === parseInt(formData.logement_id));
      
      // S'assurer qu'un service est sélectionné par défaut
      if (selectedServices.length === 0) {
        const defaultService = services.find(s => s.slug === 'attestation-logement-prise-charge');
        if (defaultService) {
          console.log('Auto-sélection du service par défaut:', defaultService.id);
          setSelectedServices([defaultService.id]);
          return; // Sortir pour laisser le prochain useEffect se déclencher
        }
      }
      
      if (!proformaData && !proformaLoading && selectedServices.length > 0 && selectedLogement) {
        // Petite temporisation pour éviter les appels trop rapides
        const timer = setTimeout(() => {
          handleGenerateProforma();
        }, 300);
        
        return () => clearTimeout(timer);
      }
    }
  }, [currentStep, services.length, logements.length, proformaData, proformaLoading, selectedServices.length, formData.logement_id]);

  const loadLogements = async () => {
    if (loading) return;
    try {
      setLoading(true);
      setGeneralError('');
      const data = await getLogementsDisponibles();
      setLogements(data);
    } catch (error) {
      console.error('Erreur chargement logements:', error);
      setGeneralError('Impossible de charger les logements disponibles.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newFormData = { ...formData, [name]: value };

    // Gérer les dépendances géographiques
    if (name === 'pays_naissance_client') {
      // Réinitialiser la ville de naissance si le pays change
      newFormData.ville_naissance_client = '';
      // Extraire le code pays depuis l'élément input (si disponible)
      const countryCode = e.target.dataset?.countryCode || '';
      setSelectedCountryCode(countryCode);
    }

    if (name === 'pays_ecole') {
      // Réinitialiser la ville d'école si le pays change
      newFormData.ville_ecole = '';
      newFormData.adresse_ecole = '';
      const schoolCountryCode = e.target.dataset?.countryCode || '';
      setSelectedSchoolCountryCode(schoolCountryCode);
    }

    setFormData(newFormData);

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    if (generalError) {
      setGeneralError('');
    }
  };

  // Fonction pour extraire le code pays depuis l'attribut data
  const getCountryCode = (fieldName) => {
    if (fieldName === 'pays_naissance_client') {
      return selectedCountryCode;
    } else if (fieldName === 'pays_ecole') {
      return selectedSchoolCountryCode;
    }
    return '';
  };

  // Fonction callback pour suivre la validation des adresses
  const handleAddressValidationChange = (fieldName, isValid) => {
    setAddressValidationStatus(prev => ({
      ...prev,
      [fieldName]: isValid
    }));
  };

  // Fonction pour gérer la réception des coordonnées GPS
  const handleCoordinatesExtracted = (coordinates) => {
    setFormData(prev => ({
      ...prev,
      coordonnees_gps_ecole: coordinates
    }));
  };

  // Fonction pour formater l'affichage des coordonnées GPS
  const formatGPSCoordinates = (coordinates) => {
    if (!coordinates) return '';

    // Format: "long:X,lat:Y,alt:Z"
    try {
      const parts = coordinates.split(',');
      if (parts.length === 3) {
        const longitude = parts[0].replace('long:', '');
        const latitude = parts[1].replace('lat:', '');
        const altitude = parts[2].replace('alt:', '');

        return `Longitude: ${parseFloat(longitude).toFixed(6)}° | Latitude: ${parseFloat(latitude).toFixed(6)}° | Altitude: ${parseFloat(altitude).toFixed(1)}m`;
      }
    } catch (error) {
      console.warn('Erreur lors du formatage des coordonnées GPS:', error);
    }

    // Format de fallback
    return coordinates;
  };

  // Helper function to check if accommodation is selected (any mode)
  const hasAccommodationSelected = () => {
    // Traditional mode: logement_id is set and not in map mode
    const hasTraditional = formData.logement_id && !mapSelectionMode;
    // Map mode: selectedMapAccommodation is set and in map mode
    const hasMap = selectedMapAccommodation && mapSelectionMode;
    return hasTraditional || hasMap;
  };

  // Fonction pour gérer la sélection d'un logement depuis la carte
  const handleMapAccommodationSelect = async (accommodationData) => {
    // Si accommodationData est null, c'est une désélection
    if (!accommodationData) {
      setSelectedMapAccommodation(null);
      setMapSelectionMode(false);
      setFormData(prev => ({ ...prev, logement_id: '' }));
      return;
    }

    setSelectedMapAccommodation(accommodationData);
    setMapSelectionMode(true);

    // Désélectionner les logements traditionnels
    if (formData.logement_id) {
      setFormData(prev => ({ ...prev, logement_id: '' }));
    }

    // Si le logement a un ID (existant ou nouvellement créé), l'assigner au formData
    if (accommodationData.id) {
      setFormData(prev => ({ ...prev, logement_id: accommodationData.id }));

      // IMPORTANT : Recharger immédiatement la liste des logements pour éviter l'erreur "n'est plus disponible"
      // Ceci est nécessaire car le nouveau logement doit être dans la liste pour la validation
      await loadLogements();
    }
  };

  // Fonction pour gérer la sélection d'un logement traditionnel
  const handleTraditionalAccommodationSelect = (logementId) => {
    setFormData(prev => ({ ...prev, logement_id: logementId }));
    setMapSelectionMode(false);
    setSelectedMapAccommodation(null);
  };


  const validateCurrentStep = () => {
    const newErrors = {};
    let isValid = true;

    if (currentStep === 1) {
      if (!formData.nom_client?.trim()) {
        newErrors.nom_client = 'Le nom est requis';
        isValid = false;
      }
      if (!formData.prenom_client?.trim()) {
        newErrors.prenom_client = 'Le prénom est requis';
        isValid = false;
      }
      if (!formData.email_client?.trim()) {
        newErrors.email_client = 'L\'email est requis';
        isValid = false;
      }
      if (!formData.telephone_client?.trim()) {
        newErrors.telephone_client = 'Le numéro de téléphone est requis';
        isValid = false;
      } else {
        // Validation avec react-phone-number-input
        const phoneValue = formData.telephone_client.trim();

        try {
          // Utiliser la validation intégrée de react-phone-number-input
          if (!isValidPhoneNumber(phoneValue)) {
            newErrors.telephone_client = 'Numéro de téléphone invalide pour le pays sélectionné';
            isValid = false;
          }
        } catch (error) {
          // Si erreur de validation, vérifier format de base
          if (!phoneValue.startsWith('+')) {
            newErrors.telephone_client = 'Le numéro doit commencer par un indicatif pays (ex: +33, +237)';
            isValid = false;
          } else if (phoneValue.length < 8 || phoneValue.length > 18) {
            newErrors.telephone_client = 'Longueur de numéro invalide';
            isValid = false;
          }
        }
      }
      if (!formData.genre_client?.trim()) {
        newErrors.genre_client = 'Le genre est requis';
        isValid = false;
      }
    }

    if (currentStep === 2) {
      if (!formData.ecole_universite?.trim()) {
        newErrors.ecole_universite = 'L\'école/université est requise';
        isValid = false;
      }
      if (!formData.filiere?.trim()) {
        newErrors.filiere = 'La filière est requise';
        isValid = false;
      }
      if (!formData.adresse_ecole?.trim()) {
        newErrors.adresse_ecole = 'L\'adresse complète de l\'école est requise';
        isValid = false;
      } else if (addressValidationStatus.adresse_ecole === false) {
        newErrors.adresse_ecole = 'Veuillez sélectionner une adresse dans la liste de suggestions';
        isValid = false;
      }
    }

    if (currentStep === 3) {
      if (!hasAccommodationSelected()) {
        setGeneralError('Veuillez sélectionner un logement depuis la liste ou la carte');
        isValid = false;
      } else {
        // Clear error if we have a valid selection
        if (generalError.includes('sélectionner un logement')) {
          setGeneralError('');
        }
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleNextStep = async () => {
    if (currentStep === 3 && logements.length === 0) {
      loadLogements();
    }

    if (validateCurrentStep()) {
      // Étape 3 spécifique: s'assurer que logement_id est configuré avant de continuer
      if (currentStep === 3 && selectedMapAccommodation && mapSelectionMode && !formData.logement_id) {
        // Force sync of map selection to formData.logement_id
        if (selectedMapAccommodation.id) {
          setFormData(prev => ({ ...prev, logement_id: selectedMapAccommodation.id }));
        }
      }

      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setGeneralError('');
      setProcessingLoader(true);
      
      const cleanData = {};
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        if (value !== '' && value !== null && value !== undefined) {
          cleanData[key] = value;
        }
        if (['nom_client', 'prenom_client', 'email_client', 'ecole_universite', 'filiere', 'logement_id', 'duree_location_mois'].includes(key)) {
          cleanData[key] = value || (key === 'duree_location_mois' ? 12 : '');
        }
      });

      console.log('selectedServices avant traitement:', selectedServices);
      
      // selectedServices contient déjà les IDs directement
      const serviceIds = selectedServices.filter(id => id !== undefined && id !== null);
      
      console.log('serviceIds après filtrage:', serviceIds);
      console.log('services disponibles:', services);
      
      // Vérifier que des services sont sélectionnés
      if (serviceIds.length === 0) {
        // Si aucun service n'est sélectionné, essayer de sélectionner le service par défaut
        const defaultService = services.find(s => s.slug === 'attestation-logement-prise-charge');
        if (defaultService) {
          serviceIds.push(defaultService.id);
          setSelectedServices([defaultService.id]);
          console.log('Service par défaut sélectionné:', defaultService.id);
        } else {
          throw new Error('Veuillez sélectionner au moins un service');
        }
      }
      
      console.log('Services IDs finaux à envoyer:', serviceIds);
      console.log('Données de souscription:', cleanData);
      
      let response;

      if (souscriptionToEdit) {
        response = await updateSouscriptionWithProforma(souscriptionToEdit.id, cleanData, serviceIds);
      } else {
        response = await createSouscriptionWithProforma(cleanData, serviceIds);
      }

      setProcessingLoader(false);
      
      // Afficher le modal de succès
      setSuccessModal({
        isOpen: true,
        title: souscriptionToEdit ? '✅ Modification réussie !' : '✅ Création réussie !',
        message: response.message,
        email: response.email_sent ? formData.email_client : null
      });
      
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      setProcessingLoader(false);
      
      // Déterminer le message d'erreur approprié
      let errorMessage = 'Une erreur inattendue s\'est produite.';
      let errorDetails = '';
      
      if (error.response?.data?.detail) {
        errorMessage = 'Échec de l\'opération';
        
        // Si c'est un tableau d'erreurs de validation (status 422)
        if (Array.isArray(error.response.data.detail)) {
          const validationErrors = error.response.data.detail;
          errorDetails = validationErrors.map(err => {
            const field = err.loc ? err.loc.join('.') : 'champ';
            return `${field}: ${err.msg}`;
          }).join(' | ');
        } 
        // Si c'est une chaîne simple
        else if (typeof error.response.data.detail === 'string') {
          errorDetails = error.response.data.detail;
        }
        // Si c'est autre chose, on convertit en string
        else {
          errorDetails = JSON.stringify(error.response.data.detail);
        }
      } else if (error.message) {
        errorDetails = error.message;
      }
      
      setErrorModal({
        isOpen: true,
        title: souscriptionToEdit ? '❌ Erreur de modification' : '❌ Erreur de création',
        message: errorMessage,
        details: errorDetails
      });
      
      setGeneralError('Une erreur s\'est produite. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Gestionnaires pour les modals
  const handleSuccessModalClose = () => {
    setSuccessModal({ isOpen: false, title: '', message: '', email: '' });
    onComplete(); // Fermer le wizard après succès
  };

  const handleErrorModalClose = () => {
    setErrorModal({ isOpen: false, title: '', message: '', details: '' });
  };

  const handleRetrySubmit = () => {
    handleErrorModalClose();
    handleSubmit(); // Relancer la soumission
  };

  const renderErrorMessage = () => {
    if (!generalError) return null;
    
    return (
      <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-orange-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-orange-800">{generalError}</p>
          </div>
          {generalError.includes('logements') && (
            <button
              onClick={() => loadLogements()}
              disabled={loading}
              className="ml-4 px-3 py-1 bg-orange-100 hover:bg-orange-200 text-orange-800 text-xs font-medium rounded-md transition-colors duration-200 disabled:opacity-50"
            >
              {loading ? 'Chargement...' : 'Réessayer'}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderInput = (name, label, type = 'text', placeholder = '', required = false, disabled = false) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-orange-500">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={formData[name] || ''}
        onChange={handleInputChange}
        disabled={disabled}
        className={`block w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${
          disabled
            ? 'bg-gray-100 text-gray-600 cursor-not-allowed border-gray-200'
            : errors[name]
              ? 'border-orange-300 focus:ring-orange-400 bg-orange-50'
              : 'border-gray-300 focus:ring-blue-400'
        }`}
        placeholder={placeholder}
        required={required}
      />
      {errors[name] && (
        <p className="mt-1 text-sm text-orange-600">{errors[name]}</p>
      )}
      {disabled && name === 'email_client' && (
        <p className="mt-1 text-xs text-gray-500">
          {user?.role === 'admin-generale' ? '⚠️ Vous êtes Admin, vous pouvez modifier' : '🔒 Email automatiquement lié à votre compte'}
        </p>
      )}
    </div>
  );

  const renderStepIndicator = () => (
    <div className="flex justify-center mb-10">
      <div className="flex items-center space-x-4">
        {[
          { num: 1, label: 'Client' },
          { num: 2, label: 'Académique' },
          { num: 3, label: 'Logement' },
          { num: 4, label: 'Récapitulatif' }
        ].map((step, index) => (
          <div key={step.num} className="flex items-center">
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                step.num <= currentStep 
                  ? 'bg-blue-500 text-white shadow-lg' 
                  : 'bg-gray-200 text-gray-500'
              }`}>
                {step.num <= currentStep ? (
                  step.num < currentStep ? '✓' : step.num
                ) : step.num}
              </div>
              <span className={`text-xs mt-2 font-medium transition-colors ${
                step.num <= currentStep ? 'text-blue-500' : 'text-gray-400'
              }`}>
                {step.label}
              </span>
            </div>
            {index < 3 && (
              <div className={`w-16 h-0.5 mx-4 transition-colors duration-300 ${
                step.num < currentStep ? 'bg-blue-500' : 'bg-gray-200'
              }`}></div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep1 = () => {
    // Déterminer si le champ email est éditable
    // Éditable uniquement si : user est admin-generale
    const isEmailEditable = user?.role === 'admin-generale';

    return (
      <div className="space-y-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-6">Informations Client</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {renderInput('nom_client', 'Nom', 'text', 'Dupont', true)}
          {renderInput('prenom_client', 'Prénom', 'text', 'Jean', true)}
          {renderInput('email_client', 'Email', 'email', 'jean.dupont@email.com', true, !isEmailEditable)}

        {/* Numéro de téléphone avec code pays */}
        <div className="md:col-span-1">
          <PhoneInput
            id="telephone_client"
            name="telephone_client"
            label="Numéro de téléphone"
            placeholder="12 34 56 78 90"
            value={formData.telephone_client}
            onChange={handleInputChange}
            required={true}
            errors={errors}
          />
        </div>

        {renderInput('date_naissance_client', 'Date de naissance', 'date')}

        {/* Genre avec radio buttons */}
        <div className="md:col-span-2">
          <GenderRadio
            id="genre_client"
            name="genre_client"
            label="Genre"
            value={formData.genre_client}
            onChange={handleInputChange}
            required={true}
            errors={errors}
          />
        </div>

        {/* Pays de naissance avec autocomplétion */}
        <GoogleAutocomplete
          id="pays_naissance_client"
          name="pays_naissance_client"
          label="Pays de naissance"
          placeholder="Ex: France, Cameroun, Canada..."
          value={formData.pays_naissance_client}
          onChange={handleInputChange}
          type="country"
          errors={errors}
        />

        {/* Ville de naissance dépendante du pays */}
        <GoogleAutocomplete
          id="ville_naissance_client"
          name="ville_naissance_client"
          label="Ville de naissance"
          placeholder="Ex: Paris, Douala, Toronto..."
          value={formData.ville_naissance_client}
          onChange={handleInputChange}
          type="city"
          countryCode={selectedCountryCode}
          disabled={!formData.pays_naissance_client}
          errors={errors}
        />

        {/* Nationalité avec sélection pays */}
        <GoogleAutocomplete
          id="nationalite_client"
          name="nationalite_client"
          label="Nationalité"
          placeholder="Ex: Française, Camerounaise, Canadienne..."
          value={formData.nationalite_client}
          onChange={handleInputChange}
          type="country"
          errors={errors}
        />

        {/* Pays de destination avec sélection pays */}
        <GoogleAutocomplete
          id="pays_destination"
          name="pays_destination"
          label="Pays de destination pour votre voyage"
          placeholder="Ex: France, Canada, Allemagne..."
          value={formData.pays_destination}
          onChange={handleInputChange}
          type="country"
          errors={errors}
        />

        {renderInput('date_arrivee_prevue', 'Date d\'arrivée à destination prévue', 'date')}
      </div>
    </div>
    );
  };

  const renderStep2 = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-800 mb-6">Informations Académiques</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderInput('ecole_universite', 'École/Université', 'text', 'Nom de l\'école ou université', true)}
        {renderInput('filiere', 'Filière', 'text', 'Filière d\'études', true)}

        {/* Pays de l'école avec autocomplétion */}
        <GoogleAutocomplete
          id="pays_ecole"
          name="pays_ecole"
          label="Pays de l'école"
          placeholder="Rechercher un pays..."
          value={formData.pays_ecole}
          onChange={handleInputChange}
          type="country"
          errors={errors}
        />

        {/* Ville de l'école avec dépendance au pays */}
        <GoogleAutocomplete
          id="ville_ecole"
          name="ville_ecole"
          label="Ville de l'école"
          placeholder="Rechercher une ville..."
          value={formData.ville_ecole}
          onChange={handleInputChange}
          type="city"
          countryCode={getCountryCode('pays_ecole')}
          disabled={!formData.pays_ecole}
          errors={errors}
        />

        {renderInput('code_postal_ecole', 'Code postal de l\'école', 'text', 'Code postal')}
        {renderInput('date_entree_prevue', 'Date de la rentrée Académique', 'date')}
        {renderInput('duree_location_mois', 'Durée de location (mois)', 'number', '12')}
      </div> 

      {/* Adresse complète avec autocomplétion et extraction GPS */}
      <GoogleAutocomplete
        id="adresse_ecole"
        name="adresse_ecole"
        label="Adresse complète de l'école"
        placeholder="Rechercher l'adresse de l'école ou université..."
        value={formData.adresse_ecole}
        onChange={handleInputChange}
        type="address"
        cityName={formData.ville_ecole}
        disabled={!formData.ville_ecole}
        required={true}
        errors={errors}
        onValidationChange={handleAddressValidationChange}
        onCoordinatesExtracted={handleCoordinatesExtracted}
      />

      {/* Affichage des coordonnées GPS extraites */}
      {formData.coordonnees_gps_ecole && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700">
            📍 Coordonnées GPS de l'école: {formatGPSCoordinates(formData.coordonnees_gps_ecole)}
          </p>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-800 mb-6">Choix du Logement</h3>
      
      {(logements.length === 0 || !formData.logement_id) && !loading && (
        <div className={`border rounded-lg p-4 mb-6 ${logements.length === 0 ? 'bg-orange-50 border-orange-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className={`h-5 w-5 ${logements.length === 0 ? 'text-orange-400' : 'text-amber-400'}`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${logements.length === 0 ? 'text-orange-800' : 'text-amber-800'}`}>
                {logements.length === 0 ? 'Aucun logement disponible' : 'Sélection de logement requise'}
              </h3>
              <div className={`mt-2 text-sm ${logements.length === 0 ? 'text-orange-700' : 'text-amber-700'}`}>
                <p>
                  {logements.length === 0 
                    ? 'Aucun logement disponible pour le moment. Impossible de continuer vers l\'étape suivante.' 
                    : 'Veuillez choisir un logement disponible pour continuer vers l\'étape suivante.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section carte pour sélection de logement */}
      {formData.coordonnees_gps_ecole && (
        <div className="space-y-4">
          {/* Titre de section */}
          <div className="text-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Sélectionnez un logement en bas de la carte
            </h3>
          </div>

          {/* Information sur la sélection carte */}
          {selectedMapAccommodation && (
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-orange-400 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <h4 className="text-sm font-medium text-orange-800">
                    Logement sélectionné sur la carte
                  </h4>
                  <div className="mt-1 text-sm text-orange-700">
                    <p className="font-semibold">{selectedMapAccommodation.title}</p>
                    <p>{selectedMapAccommodation.price}€/mois + {selectedMapAccommodation.charges}€ charges</p>
                    <p className="text-xs mt-1">📍 {selectedMapAccommodation.distance}m de l'école</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Composant carte */}
          <MapSelector
            schoolAddress={formData.adresse_ecole}
            schoolCoordinates={formData.coordonnees_gps_ecole}
            onAccommodationSelect={handleMapAccommodationSelect}
            selectedAccommodationId={selectedMapAccommodation?.id}
            className="w-full"
          />
        </div>
      )}
    </div>
  );

  const loadServices = async () => {
    try {
      const servicesData = await getAllServices(true);
      setServices(servicesData);
      // Sélectionner par défaut le service "Attestation de logement"
      const defaultService = servicesData.find(s => s.slug === 'attestation-logement-prise-charge');
      if (defaultService) {
        setSelectedServices([defaultService.id]);
      }
    } catch (error) {
      console.error('Erreur chargement services:', error);
    }
  };

  const handleServiceToggle = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleGenerateProforma = async () => {
    try {
      setProformaLoading(true);
      setGeneralError('');
      
      // Récupérer le logement sélectionné
      const selectedLogement = logements.find(l => l.id === parseInt(formData.logement_id));
      if (!selectedLogement) {
        setGeneralError('Veuillez sélectionner un logement');
        return;
      }
      
      // Préparer les données client
      const clientData = {
        nom: formData.nom_client,
        prenom: formData.prenom_client,
        email: formData.email_client,
        date_naissance: formData.date_naissance_client,
        ville_naissance_client: formData.ville_naissance_client,
        pays_naissance_client: formData.pays_naissance_client,
        nationalite: formData.nationalite_client,
        numero_passeport: formData.numero_passeport || '',
        telephone: formData.telephone_client || '',
        date_arrivee_prevue: formData.date_arrivee_prevue
      };
      
      // Préparer les données logement
      const logementData = {
        type_logement: selectedLogement.type_logement,
        adresse: selectedLogement.adresse,
        ville: selectedLogement.ville,
        prix_mois: selectedLogement.prix_mois,
        caution: selectedLogement.caution,
        frais_agence: selectedLogement.frais_agence || 0
      };
      
      const result = await generateProforma(clientData, selectedServices, logementData);
      
      if (result.success) {
        setProformaData(result);
      } else {
        setGeneralError(result.error || 'Erreur lors de la génération de la proforma');
      }
      
    } catch (error) {
      console.error('Erreur génération proforma:', error);
      setGeneralError('Erreur lors de la génération de la proforma');
    } finally {
      setProformaLoading(false);
    }
  };

  const handleDownloadProforma = () => {
    if (proformaData?.pdfBlob) {
      try {
        downloadPdf(proformaData.pdfBlob, proformaData.filename);
      } catch (error) {
        setGeneralError('Erreur lors du téléchargement');
      }
    }
  };

  const handlePreviewProforma = () => {
    if (proformaData?.pdfUrl) {
      try {
        previewPdf(proformaData.pdfUrl);
      } catch (error) {
        setGeneralError('Erreur lors de l\'ouverture de la prévisualisation');
      }
    }
  };

  const renderStep4 = () => {
    // Charger les services si pas encore fait
    if (services.length === 0) {
      loadServices();
    }

    const selectedLogement = logements.find(l => l.id === parseInt(formData.logement_id));
    const selectedServicesData = services.filter(s => selectedServices.includes(s.id));
    const totalServices = selectedServicesData.reduce((total, service) => total + service.tarif, 0);
    const totalLogement = selectedLogement ? 
      (selectedLogement.prix_mois + selectedLogement.caution + (selectedLogement.frais_agence || 0)) : 0;
    const totalGeneral = totalServices + totalLogement;

    return (
      <div className="space-y-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-6">Récapitulatif et Proforma</h3>
        
        {/* Récapitulatif */}
        <div className="bg-gradient-to-r from-blue-50 to-orange-50 rounded-lg p-6 border border-blue-200">
          <h4 className="font-semibold text-blue-700 mb-4">Récapitulatif de la Souscription</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <p><strong>Client:</strong> {formData.prenom_client} {formData.nom_client}</p>
              <p><strong>Email:</strong> {formData.email_client}</p>
              <p><strong>Nationalité:</strong> {formData.nationalite_client}</p>
            </div>
            <div>
              <p><strong>École:</strong> {formData.ecole_universite}</p>
              <p><strong>Filière:</strong> {formData.filiere}</p>
              <p><strong>Arrivée prévue:</strong> {formData.date_arrivee_prevue}</p>
            </div>
          </div>
          {selectedLogement && (
            <div className="mt-4 pt-4 border-t border-blue-200">
              <p><strong>Logement:</strong> {selectedLogement.type_logement} - {selectedLogement.ville}</p>
              <p><strong>Adresse:</strong> {selectedLogement.adresse}</p>
            </div>
          )}
        </div>



        {/* Section Proforma */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-semibold text-gray-800 mb-4">Proforma</h4>
          
          {proformaLoading ? (
            <div className="text-center py-8">
              <SpinLoader size="lg" text="Génération de la proforma en cours..." />
            </div>
          ) : proformaData ? (
            <div className="space-y-4">
              <div className="flex items-center p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <svg className="w-6 h-6 text-blue-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-blue-700">Proforma générée avec succès !</span>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={handlePreviewProforma}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200"
                >
                  📄 Prévisualiser
                </button>
                <button
                  onClick={handleDownloadProforma}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors duration-200"
                >
                  📥 Télécharger
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-500 mb-4">
                <svg className="w-12 h-12 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p>Une erreur est survenue lors de la génération automatique de la proforma.</p>
              </div>
              <button
                onClick={handleGenerateProforma}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors duration-200"
              >
                🔄 Régénérer la Proforma
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] shadow-2xl flex flex-col">
        {/* Header fixe avec titre et indicateur d'étapes */}
        <div className="flex-shrink-0">
          <div className="px-8 py-6 border-b border-gray-100">
            <h2 className="text-2xl font-bold text-gray-800">
              {souscriptionToEdit ? (
                <>
                  <span className="text-blue-500">Modifier</span> Souscription
                </>
              ) : (
                <>
                  <span className="text-blue-500">Nouvelle</span> Souscription
                </>
              )}
            </h2>
          </div>
          
          <div className="px-8 pt-6 pb-4 border-b border-gray-100">
            {renderStepIndicator()}
            {renderErrorMessage()}
          </div>
        </div>
        
        {/* Contenu scrollable - seul le contenu du step actuel */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-8">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
          </div>
        </div>
        
        {/* Footer fixe */}
        <div className="px-8 py-6 border-t border-gray-100 flex-shrink-0 bg-white rounded-b-2xl">
          <div className="flex justify-between">
            <button
              onClick={onCancel}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              Annuler
            </button>
            
            <div className="flex space-x-3">
              {currentStep > 1 && (
                <button
                  onClick={handlePrevStep}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  Précédent
                </button>
              )}
              
              {currentStep < 4 ? (
                <button
                  onClick={handleNextStep}
                  disabled={currentStep === 3 && !hasAccommodationSelected()}
                  className={`px-6 py-3 text-white rounded-lg transition-colors duration-200 ${
                    currentStep === 3 && !hasAccommodationSelected()
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                >
                  Suivant
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors duration-200"
                >
                  {loading ? 'Enregistrement...' : 'Finaliser'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ProcessingLoader 
        isOpen={processingLoader}
        message={souscriptionToEdit ? "Modification en cours..." : "Création en cours..."}
        details="Génération et envoi automatique du proforma par email"
      />
      
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={handleSuccessModalClose}
        title={successModal.title}
        message={successModal.message}
        email={successModal.email}
      />
      
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={handleErrorModalClose}
        onRetry={handleRetrySubmit}
        title={errorModal.title}
        message={errorModal.message}
        details={errorModal.details}
      />
    </div>
  );
};

export default WizardSouscription;